import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Upload } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", company: "", message: "" };
const MAX_CV = 5 * 1024 * 1024;

export default function LeadDialog({ open, onOpenChange, mode, job }) {
  const isRecruit = mode === "recruit";
  const [form, setForm] = useState(EMPTY);
  const [cv, setCv] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(EMPTY);
      setCv(null);
      setSubmitted(false);
      setLoading(false);
    }
  }, [open, mode, job]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleCv = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setCv(null);
      return;
    }
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Le CV doit être un fichier PDF.");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_CV) {
      toast.error("Le CV ne doit pas dépasser 5 Mo.");
      e.target.value = "";
      return;
    }
    setCv(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Merci de renseigner votre nom et votre email.");
      return;
    }
    setLoading(true);
    try {
      if (isRecruit) {
        await axios.post(`${API}/recruit-requests`, {
          name: form.name,
          email: form.email,
          phone: form.phone,
          company: form.company,
          message: form.message,
        });
      } else {
        const fd = new FormData();
        fd.append("name", form.name);
        fd.append("email", form.email);
        fd.append("phone", form.phone || "");
        fd.append("company", form.company || "");
        fd.append("message", form.message || "");
        if (job) {
          fd.append("job_id", job.id);
          fd.append("job_title", job.title);
        }
        if (cv) fd.append("cv", cv);
        await axios.post(`${API}/applications`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      setSubmitted(true);
      toast.success(
        isRecruit
          ? "Votre demande a bien été envoyée !"
          : "Votre candidature a bien été envoyée !"
      );
    } catch (err) {
      toast.error("Une erreur est survenue. Merci de réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const title = isRecruit
    ? "Parlons de votre recrutement"
    : job
    ? `Postuler : ${job.title}`
    : "Envoyer ma candidature";

  const description = isRecruit
    ? "Décrivez votre besoin, un consultant vous recontacte sous 24h."
    : "Renseignez vos informations, nous revenons vers vous rapidement.";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-[#0f1e33] border border-white/10 text-white">
        {submitted ? (
          <div className="py-8 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-400">
              <CheckCircle2 size={34} />
            </div>
            <h3 className="mt-5 text-xl font-bold">Message envoyé !</h3>
            <p className="mt-2 text-slate-400">
              Merci {form.name.split(" ")[0]}, notre équipe vous recontacte
              très vite.
            </p>
            <button
              onClick={() => onOpenChange(false)}
              className="mt-6 rounded-full bg-[#F5941E] hover:bg-[#e8850e] px-8 py-3 font-semibold text-white transition-colors"
            >
              Fermer
            </button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-white">
                {title}
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                {description}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="mt-2 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Nom complet *">
                  <Input
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Jean Dupont"
                    className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-[#F5941E]"
                  />
                </Field>
                <Field label="Email *">
                  <Input
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="jean@entreprise.fr"
                    className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-[#F5941E]"
                  />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Téléphone">
                  <Input
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="06 12 34 56 78"
                    className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-[#F5941E]"
                  />
                </Field>
                <Field label={isRecruit ? "Entreprise" : "Poste recherché"}>
                  <Input
                    value={form.company}
                    onChange={update("company")}
                    placeholder={isRecruit ? "Nom de l'entreprise" : "Ex: Ingénieur"}
                    className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-[#F5941E]"
                  />
                </Field>
              </div>
              <Field
                label={isRecruit ? "Votre besoin" : "Message / motivation"}
              >
                <Textarea
                  value={form.message}
                  onChange={update("message")}
                  rows={4}
                  placeholder={
                    isRecruit
                      ? "Décrivez le poste à pourvoir, le contexte, les compétences..."
                      : "Parlez-nous de votre parcours et de vos attentes..."
                  }
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-[#F5941E] resize-none"
                />
              </Field>

              {!isRecruit && (
                <Field label="CV (PDF, optionnel - max 5 Mo)">
                  <label className="flex items-center gap-3 cursor-pointer rounded-md border border-dashed border-white/20 bg-white/5 px-4 py-3 text-sm text-slate-300 transition-colors hover:border-[#F5941E]/50 hover:bg-white/[0.07]">
                    <Upload size={18} className="text-[#F5941E] shrink-0" />
                    <span className="truncate">
                      {cv ? cv.name : "Choisir un fichier PDF..."}
                    </span>
                    <input
                      type="file"
                      accept="application/pdf,.pdf"
                      onChange={handleCv}
                      className="hidden"
                    />
                  </label>
                </Field>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#F5941E] hover:bg-[#e8850e] px-6 py-3.5 font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
              >
                {loading && <Loader2 size={18} className="animate-spin" />}
                {isRecruit ? "Envoyer ma demande" : "Envoyer ma candidature"}
              </button>

              <p className="text-xs leading-relaxed text-slate-500">
                Les informations recueillies via ce formulaire sont traitées par
                LOOKING4 afin de répondre à votre demande et de gérer nos
                échanges professionnels. Elles sont conservées pendant 3 ans à
                compter du dernier contact. Vous pouvez exercer vos droits à
                tout moment en écrivant à contact@teamlooking-4.fr. Pour en
                savoir plus, consultez notre{" "}
                <a
                  href="/politique-confidentialite"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#F5941E] hover:underline"
                >
                  Politique de confidentialité
                </a>
                .
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm text-slate-300">{label}</Label>
      {children}
    </div>
  );
}
